import React, { FC, useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Box, Typography, useTheme, ErrorState, LoadingState, styled } from '@harver/shared-ui';
import { EventAvailable } from '@harver/shared-ui/lib/icons';

import { getInterviewConfirmationDetails, ConfirmationDetails } from './service';
import { ContentCard } from '../../components/ContentCard';
import { useContent } from '../../branding/content.context';
import { Cancelled } from './Cancelled';
import { formatDay, formatTime, WeekdayTypes } from '../scheduler/service';
import CookieBanner from '../../components/CookieBanner';

const StyledEventAvailable = styled(EventAvailable)`
    color: ${({ theme }) => theme.colors.success['500']};
    font-size: ${({ theme }) => theme.sizes.xLarge};
    margin-bottom: ${({ theme }) => theme.sizes.small};
`;

function getInitialState() {
    return {
        interviewers: [],
        timezoneName: {
            short: '',
            long: '',
        },
        location: '',
        candidateName: '',
        organizationName: '',
        startTime: '',
        endTime: '',
        duration: '',
        date: '',
        isRescheduleEnabled: false,
    };
}

function renderNote(lang: string, calendarEventId: string) {
    const singleSpace = ' ';
    const links = {
        $cancel: `/interview/cancel?ref=${calendarEventId}`,
        $reschedule: `/interview/reschedule?ref=${calendarEventId}`,
    };

    const [sentence, data] = lang.split('=>');
    const formattedData = JSON.parse(data);
    const keys = Object.keys(formattedData);

    return sentence.split(' ').map((text) => {
        for (const key of keys) {
            if (key === text) {
                return (
                    <React.Fragment key={text}>
                        <Link key={text} to={links[key]} style={{ color: '#192a2d' }}>
                            {formattedData[key]}
                        </Link>
                        {singleSpace}
                    </React.Fragment>
                );
            }
        }

        return (
            <React.Fragment key={text}>
                {text}
                {singleSpace}
            </React.Fragment>
        );
    });
}

export const Confirmation: FC = () => {
    const theme = useTheme();
    const { interviewConfirmation: LANG, locale } = useContent();

    const params = new URLSearchParams(useLocation().search);
    const calendarEventId = params.get('ref') as string;

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<boolean>(false);
    const [interview, setInterview] = useState<ConfirmationDetails>(getInitialState);

    useEffect(() => {
        getInterviewConfirmationDetails(calendarEventId, locale)
            .then((details) => {
                setInterview(details);
                document.title = LANG.browserTitle;
            })
            .catch(() => {
                setError(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [calendarEventId, LANG.browserTitle, locale]);

    if (isLoading) return <LoadingState testId="interview.confirmation.loading" />;

    if (error) {
        document.title = 'Something went wrong';
        return (
            <ErrorState testId="interview.confirmation.errorScreen" onReloadClick={() => window.location.reload()} />
        );
    }

    if (interview.isCancelled) return <Cancelled />;

    const note = interview.isRescheduleEnabled
        ? renderNote(LANG.cancelOrRescheduleNote, calendarEventId)
        : renderNote(LANG.cancelNote, calendarEventId);

    return (
        <ContentCard note={note}>
            <Box
                sx={{
                    margin: `${theme.sizes.xxLarge} 0`,
                }}
            >
                <Typography
                    style={{
                        textAlign: 'center',
                        marginBottom: theme.sizes.medium,
                    }}
                    testId="title"
                    variant="h2"
                >
                    {LANG.title.replace('{organizationName}', interview.organizationName)}
                </Typography>

                <Typography
                    style={{
                        textAlign: 'center',
                        marginBottom: theme.sizes.large,
                    }}
                    variant="body2"
                >
                    {LANG.heading}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: theme.sizes.xLarge,
                        backgroundColor: theme.colors.neutral?.['025'],
                    }}
                >
                    <StyledEventAvailable />

                    <Typography variant="h3" style={{ marginBottom: theme.sizes.small }}>
                        {formatDay(interview.startTime, undefined, locale, WeekdayTypes.LONG)}
                    </Typography>

                    <Typography variant="subtitle1">
                        {formatTime(interview.startTime, locale)} - {formatTime(interview.endTime, locale)}
                    </Typography>

                    <Typography variant="caption">
                        ({interview.timezoneName.short}) {interview.timezoneName.long}
                    </Typography>
                </Box>
            </Box>
            <CookieBanner />
        </ContentCard>
    );
};
